import { Box, Grid, Divider, Typography } from "@mui/joy";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, useEffect } from "react";
import { Plus } from "lucide-react";

import { TextField, Autocomplete, Button } from "../../../../../../shared";
import SideModal from "../../../../../../components/SideModal";
import { useSupplier, useManufacturerProduct, useManufacturer } from "../../../../../../hooks/query";
import { useNotification } from "../../../../../../hooks";
import supplierInventoryResolver from "../../../../../../libs/form-validations/supplier-inventory";
import { Action } from "./Action";

interface InventoryFormProps {
    supplierId: string;
    inventoryItem?: any | null;
    onClose: () => void;
}

export function InventoryForm({ supplierId, inventoryItem, onClose }: InventoryFormProps) {
    const queryClient = useQueryClient();
    const { showSuccess, showError } = useNotification();
    const { useCreateInventory, useUpdateInventory, useDeleteInventory, useListInventory } = useSupplier();
    const { useList: useListManufacturers } = useManufacturer();
    const { useList: useListProducts } = useManufacturerProduct();
    const { data: manufacturersData } = useListManufacturers();
    const { data: existingInventoryData } = useListInventory(supplierId);
    const createInventorySvc = useCreateInventory();
    const updateInventorySvc = useUpdateInventory();
    const deleteInventorySvc = useDeleteInventory();

    const isEditing = !!inventoryItem;

    const manufacturers = (manufacturersData as any[]) || [];
    const existingInventory = (existingInventoryData as any[]) || [];

    // If editing, find the manufacturer ID from the inventory item
    const inventoryManufacturerId = inventoryItem
        ? manufacturers.find((m) => m.Name === inventoryItem.ManufacturerName)?.Id || null
        : null;

    const [selectedManufacturerId, setSelectedManufacturerId] = useState<string | null>(inventoryManufacturerId);

    // Fetch products only when a manufacturer is selected
    const { data: productsData } = useListProducts(selectedManufacturerId || undefined, undefined, true);

    const products = (productsData as any[]) || [];

    const manufacturerOptions = useMemo(
        () =>
            manufacturers.map((manufacturer) => ({
                value: manufacturer.Id,
                label: manufacturer.Name,
            })),
        [manufacturers]
    );

    const productOptions = useMemo(
        () =>
            products.map((product) => ({
                value: product.Id,
                label: `${product.Name}${product.CategoryName ? ` (${product.CategoryName})` : ""}`,
            })),
        [products]
    );

    const unitTypeOptions = useMemo(
        () => [
            { value: "bundle", label: "Bundle" },
            { value: "stick", label: "Stick" },
            { value: "each", label: "Each" },
            { value: "roll", label: "Roll" },
            { value: "box", label: "Box" },
            { value: "pallet", label: "Pallet" },
            { value: "case", label: "Case" },
            { value: "pack", label: "Pack" },
        ],
        []
    );

    const methods = useForm({
        resolver: supplierInventoryResolver,
        defaultValues: {
            ManufacturerProductId: inventoryItem?.ManufacturerProductId || "",
            SupplierUnitType: inventoryItem?.SupplierUnitType || "",
            SupplierUnitSize: inventoryItem?.SupplierUnitSize ?? undefined,
            UnitPrice: inventoryItem?.UnitPrice ?? 0,
            UnitsAvailable: inventoryItem?.UnitsAvailable ?? 0,
            MinOrderQuantity: inventoryItem?.MinOrderQuantity ?? 1,
            OrderStep: inventoryItem?.OrderStep ?? undefined,
        },
        mode: "onBlur", // Changed from "onChange" to "onBlur" to reduce validation errors while typing
        reValidateMode: "onChange", // Re-validate on change after first blur
    });

    // Convert string SupplierUnitType to object format for Autocomplete
    const watchedUnitType = methods.watch("SupplierUnitType");
    const supplierUnitTypeValue = useMemo(() => {
        if (!watchedUnitType) return null;
        if (typeof watchedUnitType === "string") {
            return unitTypeOptions.find((opt) => opt.value === watchedUnitType) || null;
        }
        return watchedUnitType;
    }, [watchedUnitType, unitTypeOptions]);

    // Set initial manufacturer and product selections when editing
    const initialManufacturer = useMemo(() => {
        if (inventoryManufacturerId && manufacturers.length > 0) {
            const manufacturer = manufacturers.find((m) => m.Id === inventoryManufacturerId);
            return manufacturer ? { value: manufacturer.Id, label: manufacturer.Name } : null;
        }
        return null;
    }, [inventoryManufacturerId, manufacturers]);

    const initialProduct = useMemo(() => {
        if (inventoryItem?.ManufacturerProductId && products.length > 0) {
            const product = products.find((p) => p.Id === inventoryItem.ManufacturerProductId);
            return product
                ? {
                      value: product.Id,
                      label: `${product.Name}${product.CategoryName ? ` (${product.CategoryName})` : ""}`,
                  }
                : null;
        }
        return null;
    }, [inventoryItem?.ManufacturerProductId, products]);

    const [selectedManufacturer, setSelectedManufacturer] = useState<{ value: string; label: string } | null>(
        initialManufacturer
    );
    const [selectedProduct, setSelectedProduct] = useState<{ value: string; label: string } | null>(initialProduct);

    // Update selections when products load (for editing mode)
    useEffect(() => {
        if (isEditing && products.length > 0 && inventoryItem && !selectedProduct) {
            const product = products.find((p) => p.Id === inventoryItem.ManufacturerProductId);
            if (product) {
                setSelectedProduct({
                    value: product.Id,
                    label: `${product.Name}${product.CategoryName ? ` (${product.CategoryName})` : ""}`,
                });
            }
        }
    }, [isEditing, products, inventoryItem, selectedProduct]);

    const onSubmit = methods.handleSubmit(async (data) => {
        try {
            // Extract product ID from object or use string directly
            const productId =
                selectedProduct?.value ||
                (typeof data.ManufacturerProductId === "string" ? data.ManufacturerProductId : "");

            if (!productId) {
                console.error("Product ID is required");
                return;
            }

            // Check for duplicate inventory item (same product, unit type, and unit size)
            const supplierUnitType = data.SupplierUnitType || null;
            // Normalize unit size to number for comparison
            const supplierUnitSize = data.SupplierUnitSize != null ? Number(data.SupplierUnitSize) : null;

            const isDuplicate = existingInventory.some((item) => {
                // When editing, exclude the current item from duplicate check
                if (isEditing && item.Id === inventoryItem?.Id) {
                    return false;
                }

                // Compare product ID
                if (item.ManufacturerProductId !== productId) {
                    return false;
                }

                // Compare unit type (handle null/undefined)
                const itemUnitType = item.SupplierUnitType || null;
                if (itemUnitType !== supplierUnitType) {
                    return false;
                }

                // Compare unit size (normalize to number and handle null/undefined)
                const itemUnitSize = item.SupplierUnitSize != null ? Number(item.SupplierUnitSize) : null;
                if (itemUnitSize !== supplierUnitSize) {
                    return false;
                }

                // All three match - it's a duplicate
                return true;
            });

            if (isDuplicate) {
                // Mark fields as touched so errors are displayed
                methods.setValue("ManufacturerProductId", data.ManufacturerProductId, {
                    shouldTouch: true,
                    shouldValidate: false,
                });
                methods.setValue("SupplierUnitType", data.SupplierUnitType, {
                    shouldTouch: true,
                    shouldValidate: false,
                });
                methods.setValue("SupplierUnitSize", data.SupplierUnitSize, {
                    shouldTouch: true,
                    shouldValidate: false,
                });

                // Set errors
                methods.setError(
                    "ManufacturerProductId",
                    {
                        type: "manual",
                        message: "This product with the same unit type and size already exists in the inventory",
                    },
                    { shouldFocus: true }
                );
                methods.setError("SupplierUnitType", {
                    type: "manual",
                    message: "Duplicate inventory item",
                });
                methods.setError("SupplierUnitSize", {
                    type: "manual",
                    message: "Duplicate inventory item",
                });
                showError("This product with the same unit type and size already exists in the inventory");
                return;
            }

            if (isEditing && inventoryItem) {
                // Update existing inventory item
                await updateInventorySvc.mutateAsync({
                    Id: inventoryItem.Id,
                    Supplier_Id: supplierId, // Pass supplier ID for duplicate check
                    ManufacturerProductId: productId, // Pass product ID for duplicate check
                    SupplierUnitType: data.SupplierUnitType || undefined,
                    SupplierUnitSize: data.SupplierUnitSize ?? undefined,
                    UnitPrice: data.UnitPrice ?? 0,
                    UnitsAvailable: data.UnitsAvailable ?? 0,
                    MinOrderQuantity: data.MinOrderQuantity ?? 1,
                    OrderStep: data.OrderStep ?? undefined,
                });
                showSuccess("Inventory item updated successfully");
            } else {
                // Create new inventory item
                await createInventorySvc.mutateAsync({
                    SupplierId: supplierId,
                    ManufacturerProductId: productId,
                    SupplierUnitType: data.SupplierUnitType || undefined,
                    SupplierUnitSize: data.SupplierUnitSize ?? undefined,
                    UnitPrice: data.UnitPrice ?? 0,
                    UnitsAvailable: data.UnitsAvailable ?? 0,
                    MinOrderQuantity: data.MinOrderQuantity ?? 1,
                    OrderStep: data.OrderStep ?? undefined,
                });
                showSuccess("Inventory item created successfully");
            }

            // Invalidate and refetch
            queryClient.invalidateQueries({ queryKey: ["supplier-inventory", supplierId] });
            onClose();
        } catch (error: any) {
            console.error("Failed to save inventory item:", error);
            showError(error?.response?.data?.Message || error?.message || "Failed to save inventory item");
        }
    });

    const handleDelete = async () => {
        if (!inventoryItem?.Id) return;

        try {
            await deleteInventorySvc.mutateAsync(inventoryItem.Id);
            queryClient.invalidateQueries({ queryKey: ["supplier-inventory", supplierId] });
            showSuccess("Inventory item deleted successfully");
            onClose();
        } catch (error: any) {
            console.error("Failed to delete inventory item:", error);
            showError(error?.response?.data?.Message || error?.message || "Failed to delete inventory item");
        }
    };

    const isLoading = createInventorySvc.isPending || updateInventorySvc.isPending || deleteInventorySvc.isPending;

    return (
        <SideModal
            open={true}
            onClose={onClose}
            title={isEditing ? "Edit Inventory Item" : "Add Inventory Item"}
            description={
                isEditing ? "Update the inventory item details." : "Enter the details for the new inventory item."
            }
            showAdditionalHeadersIcons={false}
        >
            <FormProvider {...methods}>
                <form onSubmit={onSubmit}>
                    <Box display="flex" flexDirection="column" gap={2} p={3}>
                        <Autocomplete
                            name="ManufacturerId"
                            label="Manufacturer"
                            helperText="Select a manufacturer first"
                            placeholder="Select manufacturer..."
                            options={manufacturerOptions}
                            value={selectedManufacturer}
                            disabled={isEditing}
                            getOptionLabel={(option: any) => {
                                if (typeof option === "string") return option;
                                return option?.label || option || "";
                            }}
                            isOptionEqualToValue={(option: any, value: any) => {
                                if (!option || !value) return false;
                                if (typeof option === "string" || typeof value === "string") return false;
                                return option.value === value?.value;
                            }}
                            onChange={(e: any, value: any) => {
                                setSelectedManufacturer(value);
                                setSelectedManufacturerId(value?.value || null);
                                // Reset product selection when manufacturer changes
                                setSelectedProduct(null);
                                methods.setValue("ManufacturerProductId", "", { shouldDirty: true });
                            }}
                        />

                        <Autocomplete
                            name="ManufacturerProductId"
                            label="Product"
                            helperText="Select a product from the manufacturer"
                            placeholder={selectedManufacturer ? "Select product..." : "Select manufacturer first"}
                            options={productOptions}
                            value={selectedProduct}
                            disabled={!selectedManufacturer || isEditing}
                            getOptionLabel={(option: any) => {
                                if (typeof option === "string") return option;
                                return option?.label || option || "";
                            }}
                            isOptionEqualToValue={(option: any, value: any) => {
                                if (!option || !value) return false;
                                if (typeof option === "string" || typeof value === "string") return false;
                                return option.value === value?.value;
                            }}
                            onChange={(e: any, value: any) => {
                                setSelectedProduct(value);
                                // Set the value as the object for validation, but we'll extract the ID in onSubmit
                                methods.setValue("ManufacturerProductId", value || "", { shouldDirty: true });
                            }}
                        />

                        <Grid container spacing={2}>
                            <Grid xs={6}>
                                <Autocomplete
                                    name="SupplierUnitType"
                                    label="Supplier Unit Type"
                                    helperText="How supplier packages/sells this product"
                                    placeholder="Select unit type..."
                                    options={unitTypeOptions}
                                    value={supplierUnitTypeValue}
                                    getOptionLabel={(option: any) => {
                                        if (typeof option === "string") return option;
                                        return option?.label || option || "";
                                    }}
                                    isOptionEqualToValue={(option: any, value: any) => {
                                        if (!option || !value) return false;
                                        if (typeof option === "string" || typeof value === "string") {
                                            return option === value;
                                        }
                                        return option.value === value?.value;
                                    }}
                                    onChange={(e: any, value: any) => {
                                        methods.setValue(
                                            "SupplierUnitType",
                                            typeof value === "string" ? value : value?.value || "",
                                            { shouldDirty: true }
                                        );
                                    }}
                                />
                            </Grid>
                            <Grid xs={6}>
                                <TextField
                                    name="SupplierUnitSize"
                                    label="Supplier Unit Size"
                                    helperText="Size of supplier unit in canonical units"
                                    placeholder="e.g., 4, 12"
                                    type="number"
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                            <Grid xs={6}>
                                <TextField
                                    name="UnitPrice"
                                    label="Unit Price"
                                    helperText="Price per supplier unit"
                                    placeholder="e.g., 85.00"
                                    type="number"
                                />
                            </Grid>
                            <Grid xs={6}>
                                <TextField
                                    name="UnitsAvailable"
                                    label="Units Available"
                                    helperText="Number of units currently available"
                                    placeholder="e.g., 100"
                                    type="number"
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                            <Grid xs={6}>
                                <TextField
                                    name="MinOrderQuantity"
                                    label="Minimum Order"
                                    helperText="Minimum units required to order (default: 1 = any quantity allowed)"
                                    placeholder="e.g., 1"
                                    type="number"
                                />
                            </Grid>
                            <Grid xs={6}>
                                <TextField
                                    name="OrderStep"
                                    label="Order Increment"
                                    helperText="Order must be in multiples of this (leave empty if no restriction)"
                                    placeholder="e.g., 5 means orders of 5, 10, 15..."
                                    type="number"
                                />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 1 }} />

                        <Box display="flex" gap={2} justifyContent="flex-end">
                            <Button variant="outlined" color="neutral" onClick={onClose} disabled={isLoading}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isLoading || !methods.formState.isDirty}
                                loading={isLoading}
                                variant="soft"
                                color="primary"
                                startDecorator={!isEditing && <Plus size={18} />}
                            >
                                {isEditing ? "Update Inventory Item" : "Add Inventory Item"}
                            </Button>
                        </Box>

                        {isEditing && (
                            <Action
                                onDelete={handleDelete}
                                isDeleting={deleteInventorySvc.isPending}
                                productName={inventoryItem?.ProductName}
                            />
                        )}
                    </Box>
                </form>
            </FormProvider>
        </SideModal>
    );
}
